/**
 * Mobile motion & haptics helpers for the Plenty Rydez PWA.
 * Everything degrades gracefully on browsers that don't support each API.
 */
import { useMotionValue, useTransform } from "framer-motion";

/** Vibration patterns (ms). iOS Safari ignores navigator.vibrate but doesn't error. */
export const HAPTIC = {
  tap:      8,
  light:    [6, 8, 6],
  success:  [10, 30, 20, 30, 10],
  swipe:    12,
  select:   [4, 8, 4],
};

/** Trigger a haptic pulse when the device supports it. Silent no-op otherwise. */
export function haptic(pattern = HAPTIC.tap) {
  try {
    if (typeof navigator !== "undefined" && "vibrate" in navigator) {
      navigator.vibrate(pattern);
    }
  } catch (_) { /* no-op */ }
}

/**
 * useTilt — returns motion values to bind onto a card so pointer position
 * gives a subtle 3D tilt (max ~8deg). Call reset() on pointerLeave.
 * Usage:
 *   const t = useTilt();
 *   <motion.div style={{ rotateX: t.rotateX, rotateY: t.rotateY }}
 *     onPointerMove={t.onMove} onPointerLeave={t.reset}>
 */
export function useTilt({ max = 8, glare = true } = {}) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-1, 1], [max, -max]);
  const rotateY = useTransform(x, [-1, 1], [-max, max]);
  const glareX = useTransform(x, [-1, 1], ["10%", "90%"]);
  const glareY = useTransform(y, [-1, 1], ["10%", "90%"]);
  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const py = ((e.clientY - rect.top) / rect.height) * 2 - 1;
    x.set(Math.max(-1, Math.min(1, px)));
    y.set(Math.max(-1, Math.min(1, py)));
  };
  const reset = () => { x.set(0); y.set(0); };
  return { rotateX, rotateY, glareX, glareY, onMove, reset, glareEnabled: glare };
}
